import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Twitter, Globe, Instagram, Award, Users, TrendingUp, Loader2, Pencil } from "lucide-react";
import CampaignCard from "@/components/CampaignCard";
import EditProfileModal from "@/components/EditProfileModal";
import { Button } from "@/components/ui/button";
import { fetchUser, fetchUserCampaigns, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CreatorProfile = () => {
  const { id } = useParams();
  const { user: currentUser, requireAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchUser(id), fetchUserCampaigns(id)])
      .then(([u, cs]) => { setProfile(u); setCampaigns(cs); })
      .catch(() => setProfile(false))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleFollow = () => {
    if (!requireAuth(`Sign in to follow ${profile.name}.`)) return;
    setFollowing(!following);
    toast.success(following ? "Unfollowed" : "Now following " + profile.name);
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-navy-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!profile) return <div className="min-h-[50vh] grid place-items-center text-navy-500">User not found</div>;

  const isOwnProfile = currentUser?.user_id === profile.user_id;

  return (
    <div data-testid="creator-profile-page">
      <div className="relative h-56 bg-navy-800 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 blob-saffron opacity-60 pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 blob-cream opacity-50 pointer-events-none" />
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="-mt-16 flex flex-col md:flex-row items-start md:items-end gap-6">
          <div className="h-32 w-32 rounded-3xl border-4 border-white shadow-xl overflow-hidden bg-slate-100 grid place-items-center">
            {profile.avatar_url ? (
              <img src={resolveMediaUrl(profile.avatar_url)} alt={profile.name} className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-4xl font-semibold text-slate-400">{profile.name?.charAt(0)?.toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 pt-2">
            <div className="inline-flex items-center gap-2">
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-navy-800">{profile.name}</h1>
              <span className="text-xs font-medium text-saffron-700 bg-saffron-50 rounded-full px-2 py-0.5">{profile.role}</span>
            </div>
            <div className="text-navy-500 mt-1">{profile.email}</div>
          </div>
          <div className="flex items-center gap-2">
            {isOwnProfile ? (
              <Button onClick={() => setEditOpen(true)} data-testid="edit-profile-btn" className="rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-11 px-6">
                <Pencil className="h-4 w-4 mr-2" /> Edit profile
              </Button>
            ) : (
              <>
                <Button
                  variant={following ? "outline" : "default"}
                  onClick={toggleFollow}
                  className={`rounded-2xl h-11 px-6 ${!following ? "bg-saffron-600 hover:bg-saffron-700" : ""}`}
                  data-testid="follow-creator-btn"
                >
                  {following ? "Following" : "Follow"}
                </Button>
                <Button variant="outline" className="rounded-2xl h-11">Message</Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl border border-warm-200 bg-white p-6">
              <h3 className="font-display font-semibold text-navy-800">About</h3>
              <p className="mt-3 text-navy-600 leading-relaxed">{profile.bio || "This creator hasn't added a bio yet."}</p>
              <div className="mt-5 flex gap-2">
                {[Twitter, Globe, Instagram].map((Icon, i) => (
                  <a key={i} href="#" className="grid place-items-center h-10 w-10 rounded-xl border border-warm-200 text-navy-600 hover:text-saffron-600 hover:border-saffron-200 transition-colors" aria-label="Social">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-warm-200 bg-white p-6">
              <h3 className="font-display font-semibold text-navy-800">Stats</h3>
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-navy-500 inline-flex items-center gap-2"><Users className="h-4 w-4" /> Followers</span>
                  <span className="font-display font-semibold">{profile.stats?.followers?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-navy-500 inline-flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Total raised</span>
                  <span className="font-display font-semibold">${((profile.stats?.raised || 0) / 1000).toFixed(0)}K</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-navy-500 inline-flex items-center gap-2"><Award className="h-4 w-4" /> Campaigns</span>
                  <span className="font-display font-semibold">{profile.stats?.campaigns || 0}</span>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <h2 className="font-display text-2xl font-semibold text-navy-800">Campaigns by {profile.name.split(" ")[0]}</h2>
            {campaigns.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-navy-500">
                No campaigns yet.
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {campaigns.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
              </div>
            )}
          </div>
        </div>
        <div className="h-24" />
      </div>

      {isOwnProfile && <EditProfileModal open={editOpen} onOpenChange={setEditOpen} />}
    </div>
  );
};

export default CreatorProfile;
